import React, { useEffect, useMemo } from "react"

import { OneVerifyFlagType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"

import { POLICIES_CONFIG } from "../../../config/policies.config"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { policiesGetReportsAggregationsFlags } from "../../../store/thunks/policies.thunks"
import type { IReportsAggregatedFlagsCalculations } from "../../../types/objects.types.ts"
import type { IReportsFlagsVisualizationComponentProps } from "../../../types/props.types.ts"
import { ReportsFlagsAggregationCardComponent } from "./reportsFlagsAggregationCard.component"

export const ReportsFlagsVisualizationComponent: React.FunctionComponent<
    IReportsFlagsVisualizationComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const { reportsFilters, reportsAggregationsFlags } =
        useAppSelector(policiesSelector)

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isReportsAggregationsFlagsLoading()
    }, [runtimeState.isLoading])

    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        const thunkPromise = dispatch(
            policiesGetReportsAggregationsFlags({
                practice: selectedPracticeRole.practice,
                payload: {
                    filters: reportsFilters,
                },
            }),
        )

        return () => {
            thunkPromise.abort()
        }
    }, [selectedPracticeRole?.practice.id, JSON.stringify(reportsFilters)])

    const mappedFlagsAggregations = useMemo<
        IReportsAggregatedFlagsCalculations[]
    >(() => {
        const activeNoFlagsValues =
            reportsAggregationsFlags?.active_plans_without_flags || 0

        const totalCount = reportsAggregationsFlags?.total_checks || 0

        const result = [
            {
                label: "Active plans with no alerts",
                isInlinedSubLabel: true,
                color: NIRVANA_COLORS.brand.green,
                value: activeNoFlagsValues,
                percentage: UtilHelper.getPercentageValue(
                    activeNoFlagsValues,
                    totalCount,
                ),
            },
        ] as IReportsAggregatedFlagsCalculations[]

        for (const config of POLICIES_CONFIG.reportsFlagsAggregationsConfigs) {
            const filterFlags = (
                Object.keys(
                    POLICIES_CONFIG.flagTypeMappings,
                ) as OneVerifyFlagType[]
            ).filter(flagType => {
                const severity =
                    POLICIES_CONFIG.flagTypeMappings[flagType].severity

                return (
                    severity >= config.flagSeverityLower &&
                    severity < config.flagSeverityHigher
                )
            })

            const sectionTotalSum = reportsAggregationsFlags?.[config.key] || 0

            const calculation: IReportsAggregatedFlagsCalculations = {
                label: config.label,
                color: config.color,
                filterFlags,
                value: sectionTotalSum,
                percentage: UtilHelper.getPercentageValue(
                    sectionTotalSum,
                    totalCount,
                    1,
                ),
                children: [],
            }

            // Process children
            for (const flag of filterFlags) {
                const value = reportsAggregationsFlags?.aggregations?.[flag]

                if (!value) {
                    continue
                }

                calculation.children?.push({
                    label: POLICIES_CONFIG.flagTypeMappings[flag].label,
                    value,
                    percentage: UtilHelper.getPercentageValue(
                        value,
                        totalCount,
                        1,
                    ),
                    filterFlag: flag,
                    tooltip: POLICIES_CONFIG.flagTypeMappings[flag].tooltip,
                })
            }

            result.push(calculation)
        }

        return result
    }, [reportsAggregationsFlags])

    return (
        <div
            data-produktly="reports-general-alerts-overview"
            className={`
                relative min-h-[550px] bg-brand-lilacLight
                ${props.className}
            `}
        >
            {isLoading && (
                <div
                    className="
                        absolute z-10 w-full h-full bg-brand-whiteTransparent85
                        flex items-center justify-center
                    "
                >
                    <SpinnerElement />
                </div>
            )}

            {reportsAggregationsFlags && (
                <div className="py-[40px] px-[24px]">
                    <PrimaryText size={29} lineHeight={34} weight="bold">
                        Denial Risks and Other Alerts
                    </PrimaryText>

                    {/*ITEMS with inlined label first one by one*/}
                    {mappedFlagsAggregations
                        .filter(item => !!item.isInlinedSubLabel)
                        .map((item, index) => {
                            return (
                                <ReportsFlagsAggregationCardComponent
                                    key={index}
                                    calculation={item}
                                    className="mt-[16px]"
                                />
                            )
                        })}

                    {/*ITEMS without inlined label then in row*/}
                    <div className="mt-[16px] flex">
                        {mappedFlagsAggregations
                            .filter(item => !item.isInlinedSubLabel)
                            .map((item, index) => {
                                return (
                                    <ReportsFlagsAggregationCardComponent
                                        key={index}
                                        className={`
                                            flex-1 self-stretch
                                            ${index ? "ml-[16px]" : ""}
                                        `}
                                        calculation={item}
                                        onSliceFiltered={props.onSliceFiltered}
                                    />
                                )
                            })}
                    </div>
                </div>
            )}
        </div>
    )
}
